/**
 * Identity Package P5d — the REMOTE-ORDER GATE, browser side.
 *
 * Holds the `place_token` the server mints when a guest arrives through a
 * PHYSICAL QR (`?v=1`, `?table={i}`, `?item={id}`). An order carries it; without
 * one the guest page renders BROWSE-ONLY.
 *
 * ⚠️ THIS IS NOT `guestSession`, AND THE SPLIT IS THE WHOLE FIX. The two answer
 * different questions and must never share storage or a lifetime:
 *
 *   guestSession (localStorage) — WHO the guest is. Persistent by design: the
 *     card is bookmarkable, cross-device, survives everything. That is a
 *     feature.
 *   placeSession (sessionStorage) — whether they are HERE, right now. It must
 *     NOT survive, and sessionStorage gets that for free: a bookmark opened
 *     tomorrow opens a new tab, which has no token, which is browse-only.
 *
 * Treating persistent identity as if it were presence is exactly the bug this
 * closes — a saved card sent real tickets to a kitchen with nobody in the venue.
 *
 * A CONVENIENCE CACHE, never the authority. The server re-checks the signature,
 * the venue and the expiry on every order (`PlaceToken::check`), so a hand-edited
 * sessionStorage entry buys nothing but a 422. The local expiry check exists to
 * show the right screen, not to enforce anything.
 */

const KEY = (venueCode) => `pp_place::${String(venueCode || "").toLowerCase()}`;

// Mirrors PlaceToken::TTL_SECONDS. Only used to expire the LOCAL copy early —
// the server's expiry is the one that counts, and it is inside the signature.
const TTL_MS = 4 * 60 * 60 * 1000;

/** Remember a freshly-minted token. No-ops on a falsy token or venue. */
export function savePlace(venueCode, token) {
  if (!venueCode || !token) return;
  try {
    sessionStorage.setItem(KEY(venueCode), JSON.stringify({ token, at: Date.now() }));
  } catch (_) {
    /* private mode / storage full — ordering still works for this page load,
       it just won't survive a navigation. Never fatal. */
  }
}

/**
 * The live token for this venue, or null when there is none or it has aged out
 * locally. Null is the browse-only signal for every caller.
 */
export function loadPlace(venueCode) {
  if (!venueCode) return null;
  try {
    const raw = JSON.parse(sessionStorage.getItem(KEY(venueCode)) || "null");
    if (!raw?.token) return null;
    if (Date.now() - (raw.at || 0) > TTL_MS) {
      clearPlace(venueCode);
      return null;
    }
    return raw.token;
  } catch (_) {
    return null;
  }
}

/**
 * Drop it. Called when the SERVER rejects the token — the local copy is stale or
 * forged and keeping it would show an Order button that can only fail again.
 */
export function clearPlace(venueCode) {
  try {
    sessionStorage.removeItem(KEY(venueCode));
  } catch (_) {}
}
